import {
  hasEnoughGasOrThrow,
  useProviderHub,
} from '@aries/aptos-defi/common';
import { getAriesSDK } from '@aries/aptos-defi/common/aries-sdk';
import { useLendingMarkets } from '@aries/aptos-defi/lending/market';
import { withSendTxNotify } from '@aries/aptos-defi/utils';
import { removeProfileNamePoolPrefix } from '@aries/aptos-defi/utils/remove-profile-name-pool-prefix';
import { removeProfileNamePrefix } from '@aries/aptos-defi/utils/remove-profile-name-prefix';
import useSWR from 'swr';

export const useProfileList = (walletAddress: string | undefined) => {
  const { currentNetwork } = useProviderHub();
  const { activeMarketId, activeProfileNamePrefix } = useLendingMarkets();
  const { data: profileList, mutate } = useSWR(
    [
      'ProfileList',
      walletAddress,
      currentNetwork.name,
      activeMarketId,
      activeProfileNamePrefix,
    ],
    async () => {
      if (!walletAddress) return null;

      try {
        const sdk = getAriesSDK();

        const res = await sdk.profile.Profiles.fetch.fromAddress(
          walletAddress,
        );

        const list = res.profileSigners.map(v => {
          const profileName = removeProfileNamePrefix(v.key.toString());

          const profileDisplayName = removeProfileNamePoolPrefix(
            profileName,
            activeProfileNamePrefix,
          );

          return {
            profileName,
            profileDisplayName,
            address: v.value.account,
          };
        });

        // Sort by created time.
        return list.reverse();
      } catch (err) {
        return [];
      }
    },
    { revalidateOnFocus: false, revalidateIfStale: false },
  );

  const init = async () => {
    const success = await withSendTxNotify(async () => {
      const sdk = getAriesSDK();
      hasEnoughGasOrThrow();
      return sdk.controller
        .registerUser({
          default_profile_name: `${activeProfileNamePrefix}Main Account`,
        })
        .execute();
    })();

    if (!success) {
      return false;
    }

    setTimeout(() => {
      mutate(v => v);
    }, 1200);
    return success;
  };

  const addProfile = async (profileName: string) => {
    const success = await withSendTxNotify(async () => {
      hasEnoughGasOrThrow();
      const sdk = getAriesSDK();
      return sdk.controller
        .addSubaccount({
          profile_name: `${activeProfileNamePrefix}${profileName}`,
        })
        .execute();
    })();

    if (!success) {
      return false;
    }

    setTimeout(() => {
      mutate(v => v);
    }, 1200);
    return success;
  };

  return {
    profileList,
    init,
    addProfile,
  };
};
